/**
 * Чек-листы задачи — `/v2/tasks/{id}/checklist*`.
 *
 * Дерево, а не плоский список: у пункта есть `parentId` и вложенные `items`,
 * порядок задаёт `sortIndex`. Бэк отдаёт уже собранное дерево в `result`,
 * поэтому склеивать на фронте ничего не нужно.
 *
 * Соглашения слоя те же, что в `tasks-v2.ts`: id — UUID, поля camelCase.
 */
import { http } from '@/shared/api';
import type { SchemaV2 } from '@/shared/api';

export type ChecklistItemV2 =
  SchemaV2<'internal_tasks_handler.checklistItemResponse'>;
export type ChecklistCreateV2 =
  SchemaV2<'internal_tasks_handler.checklistCreateRequest'>;
export type ChecklistUpdateV2 =
  SchemaV2<'internal_tasks_handler.checklistUpdateRequest'>;
export type ChecklistMoveV2 =
  SchemaV2<'internal_tasks_handler.checklistMoveRequest'>;

const base = (taskId: string) =>
  `/v2/tasks/${encodeURIComponent(taskId)}/checklist`;

export async function fetchChecklistV2(
  taskId: string
): Promise<ChecklistItemV2[]> {
  const { data } = await http.get<{ result?: ChecklistItemV2[] }>(base(taskId));

  return data.result ?? [];
}

/** Создаёт пункт. Вложенный — с `parentId` родителя. */
export async function createChecklistItemV2(
  taskId: string,
  payload: ChecklistCreateV2
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(base(taskId), payload);

  return data.id ?? '';
}

/**
 * Правка пункта. `title` обязателен даже когда меняется только важность —
 * так описан контракт. `members` = итоговый состав: пустой массив очищает
 * участников, `undefined` не трогает их вовсе.
 */
export function updateChecklistItemV2(
  taskId: string,
  itemId: string,
  payload: ChecklistUpdateV2
) {
  return http.patch(`${base(taskId)}/${encodeURIComponent(itemId)}`, payload);
}

export function deleteChecklistItemV2(taskId: string, itemId: string) {
  return http.delete(`${base(taskId)}/${encodeURIComponent(itemId)}`);
}

/** Переключает «выполнено». Ответ — обновлённый пункт со временем и автором. */
export async function toggleChecklistItemV2(
  taskId: string,
  itemId: string
): Promise<ChecklistItemV2> {
  const { data } = await http.post<ChecklistItemV2>(
    `${base(taskId)}/${encodeURIComponent(itemId)}/toggle`
  );

  return data;
}

/** Перенос пункта: смена родителя и/или позиции в списке. */
export function moveChecklistItemV2(
  taskId: string,
  itemId: string,
  payload: ChecklistMoveV2
) {
  return http.post(
    `${base(taskId)}/${encodeURIComponent(itemId)}/move`,
    payload
  );
}
